import type { RefundQueueRow } from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Button } from "@/components/ui/button";


import { CheckIcon, XIcon } from "lucide-react";

type RefundQueueTableProps = {
  data: RefundQueueRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  isApproving?: string | null;
  isRejecting?: string | null;
};

export function RefundQueueTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: RefundQueueTableProps) {
  const columns: TanstackReactTable.ColumnDef<RefundQueueRow>[] = [
    {
      accessorKey: "id",
      header: "ລຳດັບ",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-xs">
          {row.original.id.slice(0, 8)}…
        </span>
      ),
      size: 100,
      enableSorting: false,
    },
    {
      accessorKey: "orderNo",
      header: "ອໍເດີ",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.orderNo}</span>
      ),
      size: 130,
      enableSorting: false,
    },
    {
      accessorKey: "customerName",
      header: "ລູກຄ້າ / Wallet",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.customerName ?? "–"}
          </span>
          <span className="text-muted-foreground text-xs">
            {row.original.customerPhone}
          </span>
        </div>
      ),
      size: 180,
      enableSorting: false,
    },
    {
      accessorKey: "amountLak",
      header: "ຍອດ (LAK)",
      cell: ({ row }) => (
        <CompactNumber
          value={Number(row.original.amountLak)}
          formatOptions={{
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }}
        />
      ),
      size: 110,
      enableSorting: false,
    },
    {
      accessorKey: "reason",
      header: "ເຫດຜົນ",
      cell: ({ row }) => (
        <span className="block max-w-32 truncate text-muted-foreground text-sm" title={row.original.reason ?? undefined}>
          {row.original.reason ?? "–"}
        </span>
      ),
      size: 140,
      enableSorting: false,
    },
    createSortableColumn<RefundQueueRow>("createdAt", "ວັນທີຂໍ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const id = row.original.id;
        const approving = isApproving === id;
        const rejecting = isRejecting === id;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="default"
              size="sm"
              onClick={() => onApprove(id)}
              disabled={approving || rejecting}
            >
              {approving ? "…" : <CheckIcon className="size-4" />}
              ອະນຸມັດ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onReject(id)}
              disabled={approving || rejecting}
            >
              {rejecting ? "…" : <XIcon className="size-4" />}
              ປະຕິເສດ
            </Button>
          </div>
        );
      },
      size: 200,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<RefundQueueRow, unknown>
      noDataMessage="ບໍ່ພົບລາຍການລໍຖ້າຄືນເງິນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
